<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import { useField } from 'vee-validate';
import { onClickOutside } from '@vueuse/core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DropDownOption } from '@ui-components/DropDown';

export interface Props {
  options: DropDownOption[];
  modelValue: DropDownOption;
  name: string;
  label?: string;
  ariaLabel?: string;
  placeholder?: string;
  isAbsolute?: boolean;
  disabled?: boolean;
  additionalCssContainer?: string;
  additionalCssButton?: string;
  additionalCssDropdown?: string;
  additionalCssDropdownOptions?: string;
  validatable?: boolean;
  validateOnValueUpdate?: boolean;
  additionalCssLabel?: string;
}

interface Emits {
  (event: 'update:modelValue', value: DropDownOption): void;
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  ariaLabel: '',
  placeholder: '',
  isAbsolute: true,
  disabled: false,
  additionalCssContainer: '',
  additionalCssButton: '',
  additionalCssDropdown: '',
  additionalCssDropdownOptions: '',
  validatable: true,
  validateOnValueUpdate: true,
  additionalCssLabel: '',
});

const emit = defineEmits<Emits>();

const dropDown = ref(null);
const dropDownOptions = ref<HTMLUListElement | null>(null);
const optionsVisible = ref(false);
const focusedOption = ref<UIDropDownOption | null>(null);

const {
  errorMessage,
  meta,
  setValue,
  validate,
} = useField(props.name, undefined, {
  initialValue: props.modelValue,
  validateOnValueUpdate: props.validateOnValueUpdate,
});

const toggleOptionsVisibility = () => {
  if (props.disabled) return;

  optionsVisible.value = !optionsVisible.value;

  if (optionsVisible.value) {
    nextTick(() => {
      if (!dropDownOptions.value) return;
      const selectedItem: HTMLElement | null =
        (dropDownOptions.value as HTMLUListElement).querySelector('[aria-selected="true"]');
      dropDownOptions.value?.scrollTo({ top: (selectedItem?.offsetTop ? selectedItem.scrollTop - 50 : 0) });
    });
  }
};

const onOptionFocused = (option: DropDownOption) => {
  focusedOption.value = option;

  if (dropDownOptions.value) {
    const selectedItem: HTMLElement | null =
      (dropDownOptions.value as HTMLUListElement).querySelector(`#${slugify(option.label)}`);

    dropDownOptions.value.scrollTo({
      top: (selectedItem?.offsetTop ? selectedItem.scrollTop - 50 : 0),
      behavior: 'smooth'
    });
  }
};

const onOptionClicked = (option: DropDownOption, hideOptions = true) => {
  if (!option.isSubTitle && !props.disabled) {
    focusedOption.value = option;
    setValue(option);
    emit('update:modelValue', option);

    if (hideOptions) optionsVisible.value = false;
  }
};

const focusNextOption = () => {
  const selectedOptionIndex = props.options.findIndex(option => option.label === focusedOption.value?.label);
  const nextOption = props.options[selectedOptionIndex + 1] ?? props.options[props.options.length - 1];
  onOptionFocused(nextOption);
};

const focusPreviousOption = () => {
  const selectedOptionIndex = props.options.findIndex(option => option.label === focusedOption.value?.label);
  const previousOption = props.options[selectedOptionIndex - 1] ?? props.options[0];
  onOptionFocused(previousOption);
};

const selectOption = () => {
  if (!optionsVisible.value) {
    optionsVisible.value = true;
    return;
  }

  const selectedOption = props.options.find(option => option.label === focusedOption.value?.label);

  if (selectedOption) onOptionClicked(selectedOption);
};

onMounted(() => {
  focusedOption.value = props.modelValue;

  if (props.validatable) validate();

  onClickOutside(dropDown, () => {
    optionsVisible.value = false;
  });
});
</script>

<template>
  <div>
    <div
      ref="dropDown"
      class="relative"
      :class="props.additionalCssContainer"
    >
      <label v-if="props.label" :for="`dropdown-${name}`" :class="props.additionalCssLabel">
        {{ props.label }}
      </label>
      <button
        :id="`dropdown-${name}`"
        aria-haspopup="listbox"
        class="flex items-center justify-between rounded-xl border border-blue-300 bg-white p-2 px-4 w-full focus:shadow-active"
        :class="[{
            // eslint-disable-next-line max-len
            '!bg-orange-100 !border-orange-900 !text-orange-900 shadow-activeError focus:!border-blue-300 focus:!text-gray-900 focus:!bg-white': errorMessage,
            // eslint-disable-next-line max-len
            '!border-green-700 focus:!shadow-activeSuccess': props.modelValue?.value?.length > 0 && meta.valid && validatable,
          },
          {'pointer-events-none bg-gray-100 border-gray-100 text-gray-700': props.disabled},
          props.additionalCssButton,
        ]"
        :disabled="props.disabled"
        :data-test-id="`dropdown-${name}`"
        :aria-expanded="optionsVisible"
        @click.prevent="toggleOptionsVisibility"
        @keydown.arrow-down.arrow-right.prevent="focusNextOption"
        @keydown.arrow-up.arrow-left.prevent="focusPreviousOption"
        @keydown.enter.space.prevent="selectOption"
        @keydown.escape="toggleOptionsVisibility"
      >
        <div class="flex items-center gap-2 w-[calc(100%-1rem)]">
          <slot name="before-label" />
          <span
            class="label mb-0 mr-4 text-left truncate"
            :class="{ 'text-gray-400': !modelValue && placeholder }"
            v-text="modelValue?.label ?? placeholder ?? ''"
          />
        </div>
        <FontAwesomeIcon
          v-if="props.modelValue?.value?.length > 0 && meta.valid && validatable"
          class="text-green-700 opacity-0 transition-opacity duration-300"
          :class="{
            ['!opacity-100']: meta.valid
          }"
          :icon="['fas', 'check']"
          size="lg"
        />
        <FontAwesomeIcon v-else class="size-4 font-light" :icon="['fas', 'chevron-down']" />
      </button>

      <UIInput
        :key="name"
        :name="name"
        :model-value="modelValue?.value ?? ''"
        type="hidden"
        class="hidden"
      />

      <div
        :class="['w-full z-10', props.additionalCssDropdown, {'absolute': isAbsolute}]"
      >
        <ul
          v-show="optionsVisible"
          ref="dropDownOptions"
          role="listbox"
          :aria-label="label ?? ariaLabel"
          :aria-hidden="!optionsVisible"
          tabindex="-1"
          class="z-10 inset-x-0 mt-2 max-h-48 overflow-y-auto scroll-smooth rounded-xl border border-blue-300
          bg-white shadow-xl user-select-none"
          :class="props.additionalCssDropdownOptions"
        >
          <li
            v-for="(option, index) in props.options"
            :id="slugify(option.label)"
            :key="option.label"
            role="option"
            :aria-selected="option.value === modelValue?.value"
            class="relative flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-200 focus:bg-blue-100"
            :class="[
              { 'bg-blue-200 font-medium': option.value === focusedOption?.value },
              { 'font-medium': option.isSubTitle },
              { 'hover:font-medium hover:cursor-pointer': !option.isSubTitle }
            ]"
            :data-test-id="`dropdown-${name}-option-${index}`"
            @click.prevent="() => onOptionClicked(option)"
          >
            <slot :name="`before-option-label-${index}`" />
            <FontAwesomeIcon v-if="option.iconName" class="pr-2" :icon="['fas', option.iconName]" />
            <slot :name="`option-label-${index}`">
              {{ option.label }}
            </slot>
            <slot :name="`after-option-label-${index}`" />
          </li>
        </ul>
      </div>
    </div>
    <span
      v-if="validatable"
      class="text-sm text-orange-900 opacity-0 peer-focus:opacity-0"
      :class="{
        ['opacity-100']: errorMessage
      }"
      v-text="errorMessage ?? 'error'"
    />
  </div>
</template>
